import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { IPeripheral, IUnit } from '@app/interfaces/IPeripheral';
import { ValidationTestResult } from '@app/main/modules/foundation/_services/device-data-validation.service';
import { VehicleValidationCheckRecordService } from '@app/main/modules/foundation/_services/vehicle-validation-check-record.service';
import { NavigationService } from '@app/services/navigation.service';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { IEngineerCommission } from '../../../foundation.interfaces';
import { FoundationNavigationModule } from '../../../foundation.navigation';
import { AccountService } from '../../../_services/account.service';
import { UnitsService } from '../../../_services/unit.service';
import { UserService } from '../../../_services/user.service';
import { FoundationVehiclesService } from '../../../_services/vehicle.service';
import { CommissionService } from '../commission.service';
import './commission-detail.component.scss';
import { TranslationService } from '@app/services/translation.service';

@Component({
    selector: 'commission-detail',
    template: require('./commission-detail.component.html')
})
export class CommissionDetailComponent implements OnInit, OnDestroy {
    @Input() commission: IEngineerCommission;
    commissionId: number;

    installedDevices: (IPeripheral | IUnit)[];

    loading: boolean;
    loadingDevices: boolean;

    commissionValidationCheckRecords: ValidationTestResult[] = [];

    title: string;

    private destroy$ = new Subject<void>();
    constructor(
        private readonly accountService: AccountService,
        private readonly commissionService: CommissionService,
        private readonly navigationService: NavigationService,
        private readonly unitsService: UnitsService,
        private readonly userService: UserService,
        private readonly vehicleService: FoundationVehiclesService,
        private readonly vehicleValidationCheckRecordService: VehicleValidationCheckRecordService,
        private readonly translationService: TranslationService) { }

    ngOnInit() {
        this.commissionId = +this.navigationService.getRouteParam('commissionId');
        
        this.setTitle();
        this.loadData();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.unsubscribe();
    }

    goToDevicePage(device): void {
        if (device.Unit) {
            this.navigationService.go(`${ FoundationNavigationModule.Units}.view`, { unitId: device.Unit.Serial });
            return;
        }

        this.navigationService.go(`${ FoundationNavigationModule.Peripherals}.view`, { serial: device.Peripheral.Serial });
    }

    //--

    private loadData(): void {
        if (!this.commissionId) {
            return;
        }
        this.loading = true;
        this.commissionService.getEngineerCommissions({ Id: this.commissionId }).pipe(takeUntil(this.destroy$)).subscribe(commissions => {
            this.commission = commissions[0];
            this.getAssociatedProperties();
            this.getDevices();
        })
    }

    private getAssociatedProperties(): void {
        this.vehicleService.getVehicle(this.commission.VehicleId).pipe(take(1)).subscribe(vehicle => {
            this.commission.vehicle = vehicle;
            this.loading = false;
        });

        this.userService.getUsers({ Id: this.commission.EngineerUserId || this.commission.UserId }).pipe(take(1)).subscribe(users => {
            this.commission.engineerUser = users[0];
        });

        if (this.commission.EngineerUserId && this.commission.UserId != this.commission.EngineerUserId) {
            this.userService.getUsers({ Id: this.commission.UserId }).pipe(take(1)).subscribe(users => {
                this.commission.internalUser = users[0];
            });
        }

        this.accountService.getCustomerAccount(this.commission.AccountId).pipe(takeUntil(this.destroy$)).subscribe(account => {
            this.commission.account = account;
        });

        this.vehicleValidationCheckRecordService.getVehicleValidationCheckRecords({ CommissionId: this.commissionId }).pipe(takeUntil(this.destroy$)).subscribe(checkRecords => {
            checkRecords.forEach(cr => {

                const results = JSON.parse(cr.ValidationCheckResultJson) as ValidationTestResult[];

                results.forEach(result => {

                    this.commissionValidationCheckRecords.push(result);
                });
            });
        });
    } 


    private getDevices(): void {
        // TODO update this when serial numbers are available on metadata. We don't want to be using these legacy properties.
        //  We need to remeber when doing this that these are still used and we need to think round it
        //  e.g.If CANBusId is not filled in we don't process SensorData so need to find all the hidey holes
        this.loadingDevices = true;

        const serials: string[] = [];

        if (this.commission.CameraId) {
            serials.push(this.commission.CameraId);
        }

        if (this.commission.DigidlId) {
            serials.push(this.commission.DigidlId);
        }

        if (this.commission.CanbusId) {
            serials.push(this.commission.CanbusId);
        }

        if (this.commission.FmiId) {
            serials.push(this.commission.FmiId);
        }

        if (this.commission.TdtId) {
            serials.push(this.commission.TdtId);
        }

        if (this.commission.Serial) {
            serials.push(this.commission.Serial);
        }

        this.installedDevices = [];

        serials.forEach(serial => {
            this.unitsService.getUnitBySerial(serial).pipe(take(1)).subscribe(unit => {
                this.installedDevices.push(unit);
            })

        })

    }

    private setTitle(): void {
        this.title = this.commission?.Id?.toString() 
            ?? this.commissionId?.toString() 
            ?? this.translationService.translate("Commissions.Commission");
    }
}
